import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuinielaStore } from '../store/quinielaStore'
import { useAuthStore } from '../store/authStore'
import { 
  Calendar, 
  Trophy, 
  Loader, 
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Save,
  Download,
  XCircle,
  Award
} from 'lucide-react'
import toast from 'react-hot-toast'

export default function QuinielaPredictions() {
  const { id, participationId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { 
    fetchQuinielaById, 
    checkUserParticipation,
    submitPrediction,
    getUserPredictions,
    getQuinielaMatches,
    loading 
  } = useQuinielaStore()
  
  const [quiniela, setQuiniela] = useState(null)
  const [matches, setMatches] = useState([])
  const [predictions, setPredictions] = useState({})
  const [savedPredictions, setSavedPredictions] = useState({})
  const [loadingMatches, setLoadingMatches] = useState(true)
  const [savingAll, setSavingAll] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [participationData, setParticipationData] = useState(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (id && participationId) {
      loadData()
    }
  }, [id, participationId])

  const loadData = async () => {
    try {
      // Verificar que el usuario esté autenticado
      if (!user) {
        toast.error('Debes iniciar sesión')
        navigate('/login')
        return
      }

      // Verificar que la participación exista y pertenezca al usuario
      const participationResult = await checkUserParticipation(id, user.uid)
      const userParticipation = participationResult.participations?.find(
        p => p.id === participationId
      )
      
      if (!userParticipation) {
        toast.error('Esta participación no existe o no te pertenece')
        navigate(`/quinielas/${id}`)
        return
      }

      setParticipationData(userParticipation)

      // Cargar quiniela
      const quinielaResult = await fetchQuinielaById(id)
      if (quinielaResult.success) {
        setQuiniela(quinielaResult.data)
        
        // Cargar predicciones guardadas para esta participación
        const predictionsResult = await getUserPredictions(id, participationId)
        if (predictionsResult.success) {
          setSavedPredictions(predictionsResult.predictions)
          setPredictions(predictionsResult.predictions || {})
        }

        // Cargar partidos desde Firestore
        await loadMatches()
      } else {
        toast.error('Error al cargar la quiniela')
        navigate('/quinielas')
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Error al cargar los datos')
    }
  }

  const loadMatches = async () => {
    setLoadingMatches(true)
    try {
      const result = await getQuinielaMatches(id)
      if (result.success && result.matches.length > 0) {
        // Los partidos ya vienen ordenados por fecha desde el store
        setMatches(result.matches)
      } else {
        console.log('No matches found in Firestore')
        setMatches([])
      }
    } catch (error) {
      console.error('Error loading matches:', error)
      toast.error('Error al cargar los partidos')
      setMatches([])
    } finally {
      setLoadingMatches(false)
    }
  }

  const handlePredictionChange = (matchId, prediction) => {
    // No permitir cambios si la predicción ya fue guardada
    if (savedPredictions[matchId]?.prediction) {
      toast.info('Esta predicción ya fue guardada y no puede modificarse')
      return
    }
    
    // Solo actualizar estado local
    setPredictions(prev => ({
      ...prev,
      [matchId]: { prediction }
    }))
    setHasUnsavedChanges(true)
  }

  const handleSaveAllPredictions = async () => {
    if (!hasUnsavedChanges) {
      toast.info('No hay cambios para guardar')
      return
    }

    setSavingAll(true)
    let savedCount = 0
    let errorCount = 0

    // Guardar solo las predicciones que han cambiado
    const predictionsToSave = Object.entries(predictions).filter(
      ([matchId, pred]) => pred.prediction && savedPredictions[matchId]?.prediction !== pred.prediction
    )

    try {
      for (const [matchId, pred] of predictionsToSave) {
        const result = await submitPrediction(id, participationId, matchId, pred.prediction, true)
        if (result.success) {
          savedCount++
        } else {
          errorCount++
        }
      }

      if (errorCount === 0) {
        setSavedPredictions({ ...predictions })
        setHasUnsavedChanges(false)
        toast.success(`¡${savedCount} predicciones guardadas exitosamente!`)
      } else {
        toast.error(`${errorCount} predicciones no se pudieron guardar`)
      }
    } catch (error) {
      console.error('Error saving predictions:', error)
      toast.error('Error al guardar las predicciones')
    } finally {
      setSavingAll(false)
    }
  }

  const isPredictionSaved = (matchId) => {
    if (!predictions[matchId]?.prediction) return true // No hay predicción aún
    return savedPredictions[matchId]?.prediction === predictions[matchId]?.prediction
  }

  const hasChanges = () => {
    return Object.keys(predictions).some(matchId => !isPredictionSaved(matchId))
  }

  const canMakePredictions = () => {
    if (!quiniela) return false
    
    const now = new Date()
    const closeDate = quiniela.registrationCloseDate?.toDate?.() || new Date(quiniela.registrationCloseDate)
    
    return quiniela.status !== 'finished' && 
           quiniela.status !== 'cancelled' &&
           now < closeDate
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    const date = timestamp?.toDate?.() || new Date(timestamp)
    return date.toLocaleDateString('es-MX', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTotalPredictions = () => {
    return Object.keys(predictions).filter(matchId => predictions[matchId]?.prediction).length
  }

  const downloadPredictionsImage = async () => {
    if (Object.keys(savedPredictions).length === 0) {
      toast.error('No hay predicciones guardadas para descargar')
      return
    }

    setDownloading(true)
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Dimensiones del canvas
      const width = 800
      const matchHeight = 140
      const headerHeight = 220
      const footerHeight = 100
      const height = headerHeight + (matches.length * matchHeight) + footerHeight
      
      canvas.width = width
      canvas.height = height

      // Función para dibujar ondulaciones en un área específica
      const drawWatermarkInArea = (startY, areaHeight, color) => {
        ctx.save()
        ctx.globalAlpha = 0.08
        
        // Dibujar ondulaciones en el área específica
        for (let y = startY; y < startY + areaHeight; y += 35) {
          ctx.beginPath()
          for (let x = 0; x < width; x += 8) {
            const waveY = y + Math.sin(x * 0.025 + y * 0.015) * 12
            if (x === 0) {
              ctx.moveTo(x, waveY)
            } else {
              ctx.lineTo(x, waveY)
            }
          }
          ctx.strokeStyle = color
          ctx.lineWidth = 2.5
          ctx.stroke()
        }
        
        ctx.restore()
      }

      // Fondo principal
      const gradient = ctx.createLinearGradient(0, 0, 0, height)
      gradient.addColorStop(0, '#fef2f2')
      gradient.addColorStop(1, '#ffffff')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, height)

      // Header con degradado rojo-verde
      const headerGradient = ctx.createLinearGradient(0, 0, width, 0)
      headerGradient.addColorStop(0, '#dc2626')
      headerGradient.addColorStop(0.5, '#ef4444')
      headerGradient.addColorStop(1, '#10b981')
      ctx.fillStyle = headerGradient
      ctx.fillRect(0, 0, width, 120)
      
      // Título
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 32px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('COMPROBANTE DE PREDICCIONES', width / 2, 45)
      
      ctx.font = '18px Arial'
      ctx.fillText(quiniela.name, width / 2, 75)
      
      // URL de la plataforma
      ctx.font = 'bold 16px Arial'
      ctx.fillText('www.kiniela.mx', width / 2, 102)

      // Información de participación
      ctx.fillStyle = '#333333'
      ctx.font = '16px Arial'
      ctx.textAlign = 'left'
      ctx.fillText(`Usuario: ${user.username || user.email}`, 40, 150)
      ctx.fillText(`Puntos: ${participationData?.points || 0}`, 40, 175)
      ctx.fillText(`Predicciones: ${Object.keys(savedPredictions).length}/${matches.length}`, 40, 200)

      // Fecha de generación
      ctx.textAlign = 'right'
      ctx.fillText(new Date().toLocaleString('es-MX'), width - 40, 150)
      ctx.fillText(`ID: ${participationId.substring(0, 8)}...`, width - 40, 175)

      // Dibujar cada partido con su predicción
      let yPos = headerHeight
      matches.forEach((match, index) => {
        const prediction = savedPredictions[match.id]

        // Fondo alternado
        if (index % 2 === 0) {
          ctx.fillStyle = '#f9fafb'
        } else {
          ctx.fillStyle = '#ffffff'
        }
        ctx.fillRect(0, yPos, width, matchHeight)

        // Dibujar ondulaciones en esta fila (alternando colores)
        const watermarkColor = index % 2 === 0 ? '#10b981' : '#dc2626'
        drawWatermarkInArea(yPos, matchHeight, watermarkColor)

        // Borde superior
        ctx.fillStyle = '#e5e7eb'
        ctx.fillRect(0, yPos, width, 1)

        // Información del partido
        ctx.fillStyle = '#111827'
        ctx.font = 'bold 18px Arial'
        ctx.textAlign = 'left'
        
        // Equipos
        const homeTeam = match.teams.home.name
        const awayTeam = match.teams.away.name
        
        ctx.fillText(homeTeam, 40, yPos + 35)
        ctx.fillText(awayTeam, 40, yPos + 90)
        
        // VS
        ctx.font = 'bold 16px Arial'
        ctx.fillStyle = '#6b7280'
        ctx.textAlign = 'center'
        ctx.fillText('VS', width / 2, yPos + 62)

        // Fecha del partido
        ctx.font = '12px Arial'
        ctx.fillStyle = '#6b7280'
        ctx.textAlign = 'left'
        const matchDate = formatDate(match.date)
        ctx.fillText(matchDate, 40, yPos + 115)

        // Predicción
        if (prediction?.prediction) {
          let predictionText = ''
          let predictionColor = '#10b981'
          
          if (prediction.prediction === 'home') {
            predictionText = `✓ ${homeTeam.split(' ').slice(-1)[0]}`
          } else if (prediction.prediction === 'away') {
            predictionText = `✓ ${awayTeam.split(' ').slice(-1)[0]}`
          } else {
            predictionText = '✓ Empate'
          }

          // Rectángulo de predicción
          ctx.fillStyle = '#d1fae5'
          ctx.fillRect(width - 180, yPos + 45, 140, 35)
          
          ctx.fillStyle = predictionColor
          ctx.font = 'bold 16px Arial'
          ctx.textAlign = 'center'
          ctx.fillText(predictionText, width - 110, yPos + 68)
        } else {
          ctx.fillStyle = '#fef3c7'
          ctx.fillRect(width - 180, yPos + 45, 140, 35)
          
          ctx.fillStyle = '#92400e'
          ctx.font = 'italic 14px Arial'
          ctx.textAlign = 'center'
          ctx.fillText('Sin predicción', width - 110, yPos + 68)
        }

        yPos += matchHeight
      })

      // Footer
      ctx.fillStyle = '#f9fafb'
      ctx.fillRect(0, yPos, width, footerHeight)
      
      // Ondulaciones en footer
      drawWatermarkInArea(yPos, footerHeight, '#10b981')
      
      ctx.fillStyle = '#6b7280'
      ctx.font = '12px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('Este comprobante es válido como respaldo de tus predicciones', width / 2, yPos + 25)
      ctx.fillText('Guárdalo en un lugar seguro hasta el cierre de la quiniela', width / 2, yPos + 45)
      
      // URL en footer
      ctx.fillStyle = '#dc2626'
      ctx.font = 'bold 14px Arial'
      ctx.fillText('www.kiniela.mx', width / 2, yPos + 70)

      // Convertir canvas a imagen y descargar
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = `predicciones-${quiniela.name.replace(/\\s+/g, '-')}-${new Date().getTime()}.png`
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
        toast.success('Comprobante descargado exitosamente')
      }, 'image/png')

    } catch (error) {
      console.error('Error generating image:', error)
      toast.error('Error al generar el comprobante')
    } finally {
      setDownloading(false)
    }
  }

  const PredictionButton = ({ matchId, option, label, icon }) => {
    const isSelected = predictions[matchId]?.prediction === option
    const isSaved = isPredictionSaved(matchId)
    const hasSavedPrediction = savedPredictions[matchId]?.prediction !== undefined
    const isDisabled = !canMakePredictions() || hasSavedPrediction

    return (
      <button
        onClick={() => !isDisabled && handlePredictionChange(matchId, option)}
        disabled={isDisabled}
        className={`flex-1 p-3 rounded-lg border-2 transition-all relative ${
          isSelected
            ? isSaved 
              ? 'border-[#0F3D2E] bg-[#0F3D2E]/20 font-semibold'
              : 'border-[#D4AF37] bg-[#D4AF37]/20 font-semibold'
            : 'border-[#0F3D2E] hover:border-[#D4AF37]'
        } ${isDisabled ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="text-center">
          {icon && <div className="mb-1">{icon}</div>}
          <div className="text-sm text-[#F8F8F8]">{label}</div>
        </div>
        {isSelected && (
          isSaved ? (
            <CheckCircle 
              size={16} 
              className="absolute top-1 right-1 text-[#22C55E]" 
            />
          ) : (
            <AlertCircle 
              size={16} 
              className="absolute top-1 right-1 text-[#D4AF37]" 
            />
          )
        )}
      </button>
    )
  }

  if (loading || loadingMatches) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-[#22C55E]" size={40} />
        </div>
      </div>
    )
  }

  if (!quiniela) {
    return null
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
      {/* Header */}
      <Link
        to={`/quinielas/${id}`}
        className="inline-flex items-center gap-2 text-[#F8F8F8] hover:text-[#D4AF37] mb-6"
      >
        <ArrowLeft size={20} />
        Volver a detalles
      </Link>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">
          Predicciones
        </h1>
        <p className="text-[#F8F8F8]">{quiniela.name}</p>
        {participationData && (
          <p className="text-sm text-[#F8F8F8] mt-1">
            Participación - Puntos: {participationData.points || 0}
          </p>
        )}
      </div>

      {/* Stats Card */}
      <div className="card bg-gradient-to-r from-[#22C55E]/20 to-[#D4AF37]/10 border-2 border-[#0F3D2E] mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#F8F8F8] mb-1">Predicciones completadas</p>
            <p className="text-3xl font-bold text-[#D4AF37]">
              {getTotalPredictions()} / {matches.length}
            </p>
            {hasUnsavedChanges && (
              <p className="text-xs text-[#D4AF37] mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                Cambios sin guardar
              </p>
            )}
          </div>
          {getTotalPredictions() === matches.length && !hasUnsavedChanges && (
            <div className="bg-[#0F3D2E] text-[#F8F8F8] rounded-full p-3">
              <CheckCircle size={32} />
            </div>
          )}
          {hasUnsavedChanges && (
            <div className="bg-[#D4AF37] text-[#1A1A1A] rounded-full p-3">
              <Save size={32} />
            </div>
          )}
        </div>
        
        {!canMakePredictions() && (
          <div className="mt-4 bg-[#D4AF37]/20 border border-[#D4AF37] rounded-lg p-3">
            <p className="text-sm text-[#D4AF37] flex items-center gap-2">
              <AlertCircle size={16} />
              Las predicciones están cerradas para esta quiniela
            </p>
          </div>
        )}
      </div>

      {/* Download Predictions Button */}
      {Object.keys(savedPredictions).length > 0 && (
        <div className="mb-6">
          <button
            onClick={downloadPredictionsImage}
            disabled={downloading}
            className="btn bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#0A2D1F] w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {downloading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Generando comprobante...
              </>
            ) : (
              <>
                <Download size={20} />
                Descargar Comprobante de Predicciones
              </>
            )}
          </button>
          <p className="text-xs text-[#F8F8F8] text-center mt-2">
            Descarga una imagen de tus predicciones como respaldo
          </p>
        </div>
      )}

      {/* Matches List */}
      <div className="space-y-4">
        {matches.length === 0 ? (
          <div className="card bg-[#222222] border border-[#0F3D2E] text-center py-12">
            <Trophy className="mx-auto mb-4 text-[#D4AF37]" size={48} />
            <h3 className="text-lg font-semibold text-[#D4AF37] mb-2">
              No hay partidos disponibles
            </h3>
            <p className="text-[#F8F8F8]">
              Los partidos se cargarán próximamente
            </p>
          </div>
        ) : (
          matches.map((match) => (
            <div key={match.id} className="card bg-[#222222] border border-[#0F3D2E]">
              {/* Match Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {match.teams.home.logo && (
                      <img 
                        src={match.teams.home.logo} 
                        alt={match.teams.home.name}
                        className="w-8 h-8 object-contain"
                      />
                    )}
                    <span className="font-semibold text-[#F8F8F8]">
                      {match.teams.home.name}
                    </span>
                  </div>
                  <span className="text-[#F8F8F8] font-bold text-lg">VS</span>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-[#F8F8F8]">
                      {match.teams.away.name}
                    </span>
                    {match.teams.away.logo && (
                      <img 
                        src={match.teams.away.logo} 
                        alt={match.teams.away.name}
                        className="w-8 h-8 object-contain"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-[#F8F8F8] justify-center">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {formatDate(match.date)}
                  </span>
                  {match.venue && (
                    <span>• {match.venue}</span>
                  )}
                </div>
              </div>

              {/* Resultado del partido si está disponible */}
              {match.result?.winner && (
                <div className="mb-4 p-3 bg-[#1A1A1A] rounded-lg border border-[#D4AF37]">
                  <p className="text-xs text-[#D4AF37] mb-2 flex items-center gap-1">
                    <Award size={14} />
                    Resultado oficial
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <span className={match.result.winner === 'home' ? 'text-[#22C55E] font-bold' : 'text-[#F8F8F8]'}>
                      {match.teams.home.name}
                      {match.result.winner === 'home' && ' ✓'}
                    </span>
                    <span className="text-[#F8F8F8]">
                      {match.result.goals?.home || 0} - {match.result.goals?.away || 0}
                    </span>
                    <span className={match.result.winner === 'away' ? 'text-[#22C55E] font-bold' : 'text-[#F8F8F8]'}>
                      {match.result.winner === 'away' && '✓ '}
                      {match.teams.away.name}
                    </span>
                  </div>
                  {match.result.winner === 'draw' && (
                    <p className="text-center text-[#22C55E] text-sm mt-1 font-bold">Empate ✓</p>
                  )}
                </div>
              )}

              {/* Prediction Buttons */}
              <div className="grid grid-cols-3 gap-3">
                <PredictionButton
                  matchId={match.id}
                  option="home"
                  label={match.teams.home.name.split(' ').slice(-1)[0]} // Nombre corto
                />
                <PredictionButton
                  matchId={match.id}
                  option="draw"
                  label="Empate"
                />
                <PredictionButton
                  matchId={match.id}
                  option="away"
                  label={match.teams.away.name.split(' ').slice(-1)[0]} // Nombre corto
                />
              </div>

              {/* Indicador de acierto/error si hay resultado */}
              {match.result?.winner && savedPredictions[match.id]?.prediction && (
                <div className="mt-3">
                  {savedPredictions[match.id].prediction === match.result.winner ? (
                    <div className="bg-[#22C55E]/20 border border-[#22C55E] rounded-lg p-3 text-center">
                      <p className="text-[#22C55E] font-bold flex items-center justify-center gap-2">
                        <CheckCircle size={20} />
                        ¡ACERTASTE! +1 punto
                      </p>
                    </div>
                  ) : (
                    <div className="bg-[#EF4444]/20 border border-[#EF4444] rounded-lg p-3 text-center">
                      <p className="text-[#EF4444] font-bold flex items-center justify-center gap-2">
                        <XCircle size={20} />
                        No acertaste - 0 puntos
                      </p>
                      <p className="text-xs text-[#F8F8F8] mt-1">
                        Pronosticaste: {
                          savedPredictions[match.id].prediction === 'home' 
                            ? match.teams.home.name 
                            : savedPredictions[match.id].prediction === 'away'
                            ? match.teams.away.name
                            : 'Empate'
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Show if prediction has unsaved changes or is saved (solo si no hay resultado todavía) */}
              {!match.result?.winner && predictions[match.id]?.prediction && !isPredictionSaved(match.id) && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-[#D4AF37] flex items-center justify-center gap-1">
                    <AlertCircle size={12} />
                    Cambios sin guardar
                  </p>
                </div>
              )}
              {!match.result?.winner && savedPredictions[match.id]?.prediction && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-[#22C55E] flex items-center justify-center gap-1">
                    <CheckCircle size={12} />
                    Predicción guardada
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Save Button - Fixed at bottom */}
      {canMakePredictions() && matches.length > 0 && hasUnsavedChanges && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={handleSaveAllPredictions}
            disabled={savingAll || getTotalPredictions() !== matches.length}
            className="btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] shadow-lg px-8 py-3 text-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingAll ? (
              <>
                <Loader className="animate-spin" size={20} />
                Guardando...
              </>
            ) : (
              <>
                <Save size={20} />
                Guardar Predicciones
              </>
            )}
          </button>
          {getTotalPredictions() !== matches.length && (
            <p className="text-xs text-[#B11217] mt-2 text-center">Debes llenar todos los partidos antes de guardar.</p>
          )}
        </div>
      )}

      {/* Bottom Stats */}
      {canMakePredictions() && matches.length > 0 && (
        <div className="mt-6 card bg-[#222222] border border-[#0F3D2E]">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-[#F8F8F8]">
                {getTotalPredictions() === matches.length ? (
                  '¡Todas las predicciones completadas!'
                ) : (
                  `Faltan ${matches.length - getTotalPredictions()} predicciones`
                )}
              </p>
              <p className="text-sm text-[#F8F8F8]">
                {hasUnsavedChanges 
                  ? 'Tienes cambios sin guardar - haz clic en el botón para guardar' 
                  : 'Todas las predicciones están guardadas'
                }
              </p>
            </div>
            {getTotalPredictions() === matches.length && !hasUnsavedChanges && (
              <CheckCircle size={32} className="text-[#22C55E]" />
            )}
            {hasUnsavedChanges && (
              <AlertCircle size={32} className="text-[#D4AF37]" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
